import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { motion } from 'framer-motion';
import {
  History,
  Filter,
  Calendar,
  DollarSign,
  Target,
  Trophy,
  XCircle,
  TrendingUp,
  TrendingDown,
  Download,
  Eye,
  Search,
  Activity
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const ClientHistory = () => {
  const { user } = useAuth();
  const [filter, setFilter] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const { data: historyData, isLoading, error } = useQuery(
    ['client-history', user?.id, filter, dateRange],
    () => api.get(`/betting/history/${user?.id}`, {
      params: { type: filter === 'all' ? 'all' : filter, page: 1, limit: 100 }
    }).then(res => {
      // Transformar datos del backend al formato esperado
      const transactions = res.data.transactions || [];

      // Calcular resumen
      const summary = {
        totalTransactions: transactions.length,
        totalWinnings: transactions
          .filter(t => t.type === 'win')
          .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0),
        totalBetAmount: transactions
          .filter(t => t.type === 'bet')
          .reduce((sum, t) => sum + Math.abs(parseFloat(t.amount || 0)), 0),
        successRate: transactions.filter(t => t.type === 'bet').length > 0
          ? ((transactions.filter(t => t.type === 'win').length /
              transactions.filter(t => t.type === 'bet').length) * 100).toFixed(1)
          : 0
      };

      return { transactions, summary };
    }),
    {
      enabled: !!user?.id,
      staleTime: 30000
    }
  );

  const formatCurrency = (amount) => {
    return `$${Number(amount || 0).toLocaleString()}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CO', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'win':
        return Trophy;
      case 'bet':
        return Target;
      case 'deposit':
        return TrendingUp;
      case 'withdrawal':
      case 'elimination':
        return TrendingDown;
      default:
        return Activity;
    }
  };

  const getTransactionColor = (type) => {
    switch (type) {
      case 'win':
        return 'success';
      case 'bet':
        return 'warning';
      case 'deposit':
        return 'success';
      case 'withdrawal':
      case 'elimination':
        return 'danger';
      default:
        return 'gray';
    }
  };

  const filterOptions = [
    { value: 'all', label: 'Todas las transacciones' },
    { value: 'bets', label: 'Solo apuestas' },
    { value: 'deposits', label: 'Solo depósitos' },
    { value: 'withdrawals', label: 'Solo retiros' },
    { value: 'wins', label: 'Solo ganancias' },
    { value: 'losses', label: 'Solo pérdidas' }
  ];

  const dateRangeOptions = [
    { value: 'all', label: 'Todo el tiempo' },
    { value: '7d', label: 'Últimos 7 días' },
    { value: '30d', label: 'Últimos 30 días' },
    { value: '90d', label: 'Últimos 3 meses' },
    { value: '1y', label: 'Último año' }
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8 text-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <History className="w-12 h-12 text-danger-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
          Error al cargar historial
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          {error.response?.data?.error || 'Error de conexión'}
        </p>
      </div>
    );
  }

  const { transactions = [], summary = {} } = historyData;

  // Filter transactions based on search term
  const filteredTransactions = transactions.filter(transaction => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      transaction.event_name?.toLowerCase().includes(searchLower) ||
      transaction.type.toLowerCase().includes(searchLower) ||
      transaction.notes?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between"
      >
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            📈 Historial de Transacciones
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Revisa todas tus apuestas, depósitos y retiros
          </p>
        </div>

        <div className="flex items-center space-x-3 mt-4 sm:mt-0">
          <button className="btn-secondary">
            <Download className="w-4 h-4 mr-2" />
            Exportar
          </button>
        </div>
      </motion.div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-primary-100 dark:bg-primary-900/30 rounded-xl">
              <Activity className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
          </div>
          <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
            Total Transacciones
          </h3>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {summary.totalTransactions || 0}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-success-100 dark:bg-success-900/30 rounded-xl">
              <TrendingUp className="w-6 h-6 text-success-600 dark:text-success-400" />
            </div>
          </div>
          <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
            Total Ganancias
          </h3>
          <p className="text-2xl font-bold text-success-600 dark:text-success-400">
            {formatCurrency(summary.totalWinnings || 0)}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-warning-100 dark:bg-warning-900/30 rounded-xl">
              <Target className="w-6 h-6 text-warning-600 dark:text-warning-400" />
            </div>
          </div>
          <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
            Total Apostado
          </h3>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {formatCurrency(summary.totalBetAmount || 0)}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="card p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
              <Trophy className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
          <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
            Tasa de Éxito
          </h3>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {summary.successRate || 0}%
          </p>
        </motion.div>
      </div>

      {/* Filters and Search */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="card p-6"
      >
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar transacciones..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-10 w-full sm:w-64"
              />
            </div>

            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="input w-full sm:w-48 bg-white dark:bg-dark-800 text-gray-900 dark:text-gray-100"
            >
              {filterOptions.map(option => (
                <option key={option.value} value={option.value} className="bg-white dark:bg-dark-800 text-gray-900 dark:text-gray-100">
                  {option.label}
                </option>
              ))}
            </select>

            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="input w-full sm:w-48 bg-white dark:bg-dark-800 text-gray-900 dark:text-gray-100"
            >
              {dateRangeOptions.map(option => (
                <option key={option.value} value={option.value} className="bg-white dark:bg-dark-800 text-gray-900 dark:text-gray-100">
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="text-sm text-gray-600 dark:text-gray-400">
            Mostrando {filteredTransactions.length} de {transactions.length} transacciones
          </div>
        </div>
      </motion.div>

      {/* Transactions List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="card p-6"
      >
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-3 bg-primary-100 dark:bg-primary-900/30 rounded-xl">
            <History className="w-6 h-6 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Transacciones Recientes
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Detalle completo de todas tus operaciones
            </p>
          </div>
        </div>

        {filteredTransactions.length === 0 ? (
          <div className="text-center py-12">
            <Activity className="w-12 h-12 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              Sin transacciones
            </h4>
            <p className="text-gray-600 dark:text-gray-400">
              No se encontraron transacciones con los filtros seleccionados
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredTransactions.map((transaction, index) => {
              const Icon = getTransactionIcon(transaction.type);
              const color = getTransactionColor(transaction.type);
              const isPositive = transaction.type === 'win' || transaction.type === 'deposit' || parseFloat(transaction.amount) > 0;

              return (
                <motion.div
                  key={transaction.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.02 }}
                  className="flex items-center p-4 bg-gray-50 dark:bg-dark-800 rounded-xl hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors group"
                >
                  <div className={`p-3 rounded-xl mr-4 bg-${color}-100 dark:bg-${color}-900/30`}>
                    <Icon className={`w-5 h-5 text-${color}-600 dark:text-${color}-400`} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {transaction.description ||
                           (transaction.type === 'bet' ? 'Apuesta realizada' :
                            transaction.type === 'win' ? 'Ganancia' :
                            transaction.type === 'deposit' ? 'Depósito' :
                            transaction.type === 'withdrawal' ? 'Retiro' : 'Transacción')
                          }
                        </h4>
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          {formatDate(transaction.created_at)}
                        </p>
                      </div>

                      <div className="flex items-center space-x-4">
                        <span className={`text-lg font-bold ${
                          isPositive
                            ? 'text-success-600 dark:text-success-400'
                            : 'text-danger-600 dark:text-danger-400'
                        }`}>
                          {isPositive ? '+' : ''}{formatCurrency(transaction.amount)}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        {transaction.event_name && (
                          <span className="text-xs px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full">
                            {transaction.event_name}
                          </span>
                        )}
                        {transaction.step_number && (
                          <span className="text-xs text-gray-600 dark:text-gray-400">
                            Paso {transaction.step_number}
                          </span>
                        )}
                      </div>

                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        Saldo: {formatCurrency(transaction.balance_after)}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default ClientHistory;